import { useEffect, useState } from 'react';
import { useAuth } from 'zite-auth-sdk';
import { getNotifications, markNotificationsRead, GetNotificationsOutputType } from 'zite-endpoints-sdk';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Bell, ShieldCheck, BookOpen, User, Star, FileText, CheckCheck } from 'lucide-react';
import { Link } from 'react-router-dom';
import { formatDistanceToNow, parseISO } from 'date-fns';

type Notification = GetNotificationsOutputType['notifications'][0];

const typeIcon: Record<string, React.FC<{ className?: string }>> = {
  Compliance: ShieldCheck,
  Training: BookOpen,
  'Edit Request': User,
  Review: Star,
  Document: FileText,
  General: Bell,
};

const timeAgo = (d?: string) => {
  if (!d) return '';
  try { return formatDistanceToNow(parseISO(d), { addSuffix: true }); } catch { return ''; }
};

export default function Notifications() {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(true);
  const [markingAll, setMarkingAll] = useState(false);

  const load = () => {
    if (!user) return;
    getNotifications({}).then(d => setNotifications(d.notifications)).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [user]);

  const markAll = async () => {
    setMarkingAll(true);
    try {
      await markNotificationsRead({ all: true });
      setNotifications(ns => ns.map(n => ({ ...n, read: true })));
    } finally {
      setMarkingAll(false);
    }
  };

  const markOne = async (id: string) => {
    await markNotificationsRead({ ids: [id] });
    setNotifications(ns => ns.map(n => n.id === id ? { ...n, read: true } : n));
  };

  const unread = notifications.filter(n => !n.read).length;

  if (loading) {
    return (
      <div className="px-6 md:px-8 py-8 max-w-2xl mx-auto space-y-3">
        {[...Array(5)].map((_, i) => <Skeleton key={i} className="h-16" />)}
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="bg-card border-b border-border">
        <div className="px-6 md:px-8 py-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <h1 className="text-xl font-bold text-foreground">Notifications</h1>
            {unread > 0 && (
              <span className="text-xs bg-primary text-primary-foreground px-2 py-0.5 rounded-full font-semibold">{unread}</span>
            )}
          </div>
          {unread > 0 && (
            <Button variant="ghost" size="sm" onClick={markAll} disabled={markingAll} className="text-xs text-primary">
              <CheckCheck className="w-3.5 h-3.5 mr-1" />
              Mark all as read
            </Button>
          )}
        </div>
      </div>

      <div className="px-6 md:px-8 py-4 max-w-2xl">
        {notifications.length === 0 ? (
          <div className="text-center py-16 text-muted-foreground">
            <Bell className="w-10 h-10 mx-auto mb-3 opacity-30" />
            <p className="text-sm">No notifications yet.</p>
          </div>
        ) : (
          <div className="space-y-1">
            {notifications.map(n => {
              const Icon = typeIcon[n.type] || Bell;
              const content = (
                <div
                  key={n.id}
                  onClick={() => { if (!n.read) markOne(n.id); }}
                  className={`flex items-start gap-3 px-4 py-3 rounded-lg transition-colors cursor-pointer ${!n.read ? 'bg-primary/5 border-l-2 border-primary' : 'hover:bg-muted/30'}`}
                >
                  <Icon className={`w-4 h-4 mt-0.5 shrink-0 ${!n.read ? 'text-primary' : 'text-muted-foreground'}`} />
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm ${!n.read ? 'text-foreground font-medium' : 'text-foreground'}`}>{n.title}</p>
                    {n.message && <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{n.message}</p>}
                    <p className="text-xs text-muted-foreground mt-1">{timeAgo(n.createdAt)}</p>
                  </div>
                </div>
              );

              return n.link ? (
                <Link key={n.id} to={n.link} className="block">{content}</Link>
              ) : (
                <div key={n.id}>{content}</div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
